import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { toastHelpers } from '@/lib/toast-helpers'
import { formatCurrency } from '@/lib/utils'
import { Banknote, Lock, Unlock, MinusCircle } from 'lucide-react'
import type { CashShiftSummary } from '@/types'

export function CashRegisterPanel() {
  const queryClient = useQueryClient()
  const [openingFloat, setOpeningFloat] = useState('')
  const [withdrawAmount, setWithdrawAmount] = useState('')
  const [withdrawNotes, setWithdrawNotes] = useState('')
  const [countedCash, setCountedCash] = useState('')
  const [closeNotes, setCloseNotes] = useState('')

  const { data: shiftResponse, isLoading } = useQuery({
    queryKey: ['cashShift'],
    queryFn: () => apiClient.getCashShift(),
    refetchInterval: 10000,
  })

  const shift = shiftResponse?.data as CashShiftSummary | null | undefined

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['cashShift'] })

  const openMutation = useMutation({
    mutationFn: () => apiClient.openCashShift(parseFloat(openingFloat) || 0),
    onSuccess: () => {
      setOpeningFloat('')
      invalidate()
      toastHelpers.success('Caja abierta', 'Turno iniciado correctamente')
    },
    onError: (e: Error) => toastHelpers.error('Error', e.message),
  })

  const withdrawMutation = useMutation({
    mutationFn: () => apiClient.withdrawCash(parseFloat(withdrawAmount) || 0, withdrawNotes || undefined),
    onSuccess: () => {
      setWithdrawAmount('')
      setWithdrawNotes('')
      invalidate()
      toastHelpers.success('Retiro registrado')
    },
    onError: (e: Error) => toastHelpers.error('Error', e.message),
  })

  const closeMutation = useMutation({
    mutationFn: () => apiClient.closeCashShift(parseFloat(countedCash) || 0, closeNotes || undefined),
    onSuccess: (res) => {
      const diff = res.data?.cash_difference ?? 0
      setCountedCash('')
      setCloseNotes('')
      invalidate()
      toastHelpers.success(
        'Corte de caja cerrado',
        `Diferencia: ${formatCurrency(diff)}${diff === 0 ? ' — cuadra perfecto' : diff > 0 ? ' — sobra en caja' : ' — falta en caja'}`
      )
    },
    onError: (e: Error) => toastHelpers.error('Error', e.message),
  })

  if (isLoading) {
    return <div className="p-8 text-center text-muted-foreground">Cargando caja...</div>
  }

  if (!shift) {
    return (
      <Card className="max-w-md mx-auto">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Unlock className="w-5 h-5" />
            Apertura de caja
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Cuenta el efectivo inicial en el cajón (cambio/monedas) antes de empezar el turno.
          </p>
          <div>
            <label className="text-sm font-medium">Fondo de caja / cambio inicial</label>
            <Input
              type="number"
              step="0.01"
              placeholder="0.00"
              value={openingFloat}
              onChange={(e) => setOpeningFloat(e.target.value)}
            />
          </div>
          <Button
            className="w-full"
            onClick={() => openMutation.mutate()}
            disabled={openMutation.isPending || !openingFloat}
          >
            Abrir caja
          </Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4 max-w-lg mx-auto">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Banknote className="w-5 h-5" />
            Caja abierta
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div className="flex justify-between"><span>Fondo inicial (cambio)</span><span>{formatCurrency(shift.opening_float)}</span></div>
          <div className="flex justify-between"><span>Ventas en efectivo</span><span className="text-green-600">+{formatCurrency(shift.cash_sales)}</span></div>
          <div className="flex justify-between"><span>Retiros</span><span className="text-red-600">-{formatCurrency(shift.withdrawals)}</span></div>
          <div className="flex justify-between font-bold text-lg border-t pt-2 mt-2">
            <span>Efectivo esperado en caja</span>
            <span>{formatCurrency(shift.expected_cash)}</span>
          </div>
          {(shift.order_count ?? 0) > 0 && (
            <div className="flex justify-between text-muted-foreground pt-1">
              <span>Pedidos en este turno</span>
              <span>{shift.order_count}</span>
            </div>
          )}
          {(shift.active_order_count ?? 0) > 0 && (
            <p className="text-sm text-amber-600 font-medium pt-2">
              {shift.active_order_count} pedido(s) activo(s) — ciérralos o cobra antes de cerrar caja
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><MinusCircle className="w-4 h-4" />Retiro de efectivo</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <Input type="number" step="0.01" placeholder="Monto" value={withdrawAmount} onChange={(e) => setWithdrawAmount(e.target.value)} />
          <Input placeholder="Motivo (opcional)" value={withdrawNotes} onChange={(e) => setWithdrawNotes(e.target.value)} />
          <Button variant="outline" className="w-full" onClick={() => withdrawMutation.mutate()} disabled={!withdrawAmount || withdrawMutation.isPending}>
            Registrar retiro
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Lock className="w-4 h-4" />
            Corte de caja (cierre)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Cuenta todo el efectivo físico en el cajón y compáralo con lo esperado ({formatCurrency(shift.expected_cash)}).
          </p>
          <Input type="number" step="0.01" placeholder="Efectivo contado" value={countedCash} onChange={(e) => setCountedCash(e.target.value)} />
          {countedCash && (
            <p className={`text-sm font-medium ${parseFloat(countedCash) - shift.expected_cash === 0 ? 'text-green-600' : 'text-amber-600'}`}>
              Diferencia: {formatCurrency(parseFloat(countedCash) - shift.expected_cash)}
            </p>
          )}
          <Input placeholder="Notas de cierre (opcional)" value={closeNotes} onChange={(e) => setCloseNotes(e.target.value)} />
          <Button
            className="w-full"
            variant="destructive"
            onClick={() => closeMutation.mutate()}
            disabled={!countedCash || closeMutation.isPending || (shift.active_order_count ?? 0) > 0}
          >
            Cerrar caja
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}
